<template>
  <card>
    <card title="Search offer" class="slds-card_boundary">
      <template #actions>
        <button class="slds-button slds-button_brand" :disabled="!offerExternalId" @click="search">Search</button>
      </template>
      <Input label="External ID" v-model="offerExternalId" @keyup-enter="search" />
    </card>
    <card :title="offer.offerId" class="slds-card_boundary" v-if="offer">
      <card title="Detail" class="slds-card_boundary">
        <data-table
          :columns="columns"
          :rows="tableRows"
          mode="view" />
      </card>
      <card title="Sections" class="slds-card_boundary">
        <accordion>
          <accordion-item :title="`${section.sectionId}`" v-for="section in offer.sections" :key="section.sectionId">
            <card class="slds-card_boundary" has-empty-title>
              <template #actions>
                <button class="slds-button slds-button_brand" :disabled="!section.mvsList || !section.mvsList.length" @click="openMvsModal(section.mvsList)">Search MVS</button>
              </template>
              <accordion>
                <accordion-item title="Detail">
                  <div class="slds-form" role="list">
                    <div class="slds-form__row" v-for="[field, value] in Object.entries(section)" :key="field">
                      <div class="slds-form__item" role="listitem" v-if="sectionFields.includes(field)">
                        <div class="slds-form-element slds-form-element_horizontal slds-is-editing">
                          <label class="slds-form-element__label">
                            {{field}}
                          </label>
                          <div class="slds-form-element__control">
                            <span class="slds-text-font_monospace">{{value}}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </accordion-item>
                <accordion-item title="MVS" v-if="section.mvsList && section.mvsList.length">
                  {{ section.mvsList }}
                </accordion-item>
                <accordion-item title="VIN" v-if="section.vinList && section.vinList.length">
                  {{ section.vinList }}
                </accordion-item>
              </accordion>
            </card>
          </accordion-item>
        </accordion>
      </card>
    </card>

    <modal
      @close="hideModal"
      @positiveButtonClick="searchMvs(mvsToSearch)"
      positive-button-text="Search"
      title="Search MVS"
      v-if="showMvsModal">

      <Input label="MVS" v-model="mvsToSearch" v-if="showMvsInput" @keyup-enter="searchMvs(mvsToSearch)" />
      <p v-else>{{result}}</p>
    </modal>
  </card>
</template>

<script>
import _ from 'lodash'
import { mapActions } from 'vuex'
import Accordion from '../components/slds/Accordion'
import AccordionItem from '../components/slds/AccordionItem'
import Card from '../components/slds/Card'
import DataTable from '../components/slds/DataTable2'
import Input from '../components/slds/Input'
import Modal from '../components/Modal'

export default {
  methods: {
    openMvsModal (list) {
      this.selectedMvsList = list
      this.showMvsModal = true
      this.result = ''
      this.showMvsInput = true
    },
    hideModal () {
      this.showMvsModal = false
      this.showmvsInput = true
      this.result = ''
      this.mvsToSearch = ''
    },
    ...mapActions({
      getOffer: 'campaignDesigner/getOffer'
    }),
    getSectionsTableRows (sectionId) {
      return this.offer.sections.find(section => section.sectionId === sectionId)
    },
    async search () {
      this.offer = undefined
      try {
        const { offer } = await this.getOffer(this.offerExternalId)
        this.offer = offer
      } catch (error) {
        this.showToast('error', this.$t('general.error'), 'text', error)
      }
    },
    searchMvs (mvs) {
      if (mvs) {
        const trim = mvs.trim()
        const found = !!this.selectedMvsList.includes(trim)
        if (found) {
          this.result = 'MVS has been found'
        } else {
          this.result = 'MVS has not been found'
        }

        this.showMvsInput = false
      }
    }
  },
  components: {
    Accordion,
    AccordionItem,
    Card,
    DataTable,
    Input,
    Modal
  },
  computed: {
    sectionTableHeaders () {
      if (this.offer.detail) {
        return {
          names: this.sectionFields,
          options: Object.keys(this.sectionFields).map(x => 'text')
        }
      } else {
        return {}
      }
    },
    tableRows () {
      if (this.offer) {
        return [_.pick(this.offer.detail, this.offerFields)]
      } else {
        return []
      }
    }
  },
  data () {
    return {
      columns: [
        {
          name: 'codMarket',
          label: 'Market Code',
          type: 'text'
        },
        {
          name: 'title',
          type: 'text',
          label: 'Title'
        },
        {
          name: 'description',
          type: 'text',
          label: 'Description'
        },
        {
          name: 'externalTitle',
          type: 'text',
          label: 'External Title'
        },
        {
          name: 'externalDescription',
          type: 'text',
          label: 'External Description'
        },
        {
          name: 'masterKey',
          type: 'text',
          label: 'MasterKey'
        },
        {
          name: 'startDate',
          type: 'date',
          label: 'Start Date'
        },
        {
          name: 'endDate',
          type: 'date',
          label: 'End Date'
        },
        {
          name: 'currency',
          type: 'text',
          label: 'Currency'
        },
        {
          name: 'createdDate',
          type: 'date',
          label: 'Created Date'
        }
      ],
      mvsToSearch: '',
      offer: undefined,
      offerExternalId: '',
      offerFields: [
        'codMarket',
        'title',
        'description',
        'externalTitle',
        'externalDescription',
        'masterKey',
        'startDate',
        'endDate',
        'currency',
        'createdDate'
      ],
      result: '',
      sectionFields: [
        'sectionId',
        'scoreChurn',
        'brand',
        'fuelType',
        'modelYearFrom',
        'engineSize',
        'segment',
        'fleet',
        'kmFrom',
        'kmTo',
        'timeRangeSelector',
        'range',
        'offerType',
        'priceValue',
        'sectionTitle',
        'sectionDescription',
        'includeOrExcludeVins'
      ],
      selectedMvsList: [],
      showMvsInput: false,
      showMvsModal: false
    }
  }
}
</script>
